import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useBrandContext } from '../../context/BrandContext.jsx';

const BrandStyleProvider = ({ children }) => {
  const brandConfig = useBrandContext();

  useEffect(() => {
    // Si aucune marque n'est détectée, ne pas appliquer de styles
    if (!brandConfig) {
      return;
    }

    // Injecter toutes les variables CSS dans le document
    const root = document.documentElement;
    const design = brandConfig.design;

    // Couleurs principales
    root.style.setProperty('--brand-primary-color', design.primaryColor);
    root.style.setProperty('--brand-secondary-color', design.secondaryColor);
    root.style.setProperty('--brand-accent-color', design.accentColor);

    // Couleurs de texte
    root.style.setProperty('--brand-text-primary', design.textPrimary);
    root.style.setProperty('--brand-text-secondary', design.textSecondary);
    root.style.setProperty('--brand-text-rgpd', design.textRgpdColor);
    root.style.setProperty('--brand-text-disabled', design.textDisabled);
    root.style.setProperty('--brand-text-placeholder', design.textPlaceholder);

    // Couleurs de fond
    root.style.setProperty(
      '--brand-background-primary',
      design.backgroundPrimary,
    );
    root.style.setProperty(
      '--brand-background-secondary',
      design.backgroundSecondary,
    );
    root.style.setProperty('--brand-background-hover', design.backgroundHover);
    root.style.setProperty(
      '--brand-background-selected',
      design.backgroundSelected,
    );

    // Couleurs des bordures
    root.style.setProperty('--brand-border-primary', design.borderPrimary);
    root.style.setProperty('--brand-border-secondary', design.borderSecondary);
    root.style.setProperty('--brand-border-focus', design.borderFocus);

    // Couleurs d'état
    root.style.setProperty('--brand-success-color', design.successColor);
    root.style.setProperty('--brand-error-color', design.errorColor);
    root.style.setProperty('--brand-warning-color', design.warningColor);
    root.style.setProperty('--brand-info-color', design.infoColor);

    // Couleurs des boutons
    root.style.setProperty('--brand-button-primary', design.buttonPrimary);
    root.style.setProperty(
      '--brand-button-primary-hover',
      design.buttonPrimaryHover,
    );
    root.style.setProperty('--brand-button-secondary', design.buttonSecondary);
    root.style.setProperty(
      '--brand-button-secondary-hover',
      design.buttonSecondaryHover,
    );
    root.style.setProperty('--brand-button-disabled', design.buttonDisabled);

    // Couleurs du calendrier
    root.style.setProperty('--brand-calendar-header', design.calendarHeader);
    root.style.setProperty('--brand-calendar-day', design.calendarDay);
    root.style.setProperty(
      '--brand-calendar-day-hover',
      design.calendarDayHover,
    );
    root.style.setProperty(
      '--brand-calendar-day-selected',
      design.calendarDaySelected,
    );
    root.style.setProperty(
      '--brand-calendar-day-disabled',
      design.calendarDayDisabled,
    );
    root.style.setProperty(
      '--brand-calendar-day-hover-text',
      design.calendarDayHoverText || design.textSecondary,
    );
    root.style.setProperty(
      '--brand-calendar-day-selected-text',
      design.calendarDaySelectedText || design.textSecondary,
    );

    // Couleurs des créneaux horaires
    root.style.setProperty('--brand-time-slot', design.timeSlot);
    root.style.setProperty('--brand-time-slot-hover', design.timeSlotHover);
    root.style.setProperty(
      '--brand-time-slot-selected',
      design.timeSlotSelected,
    );
    root.style.setProperty(
      '--brand-time-slot-disabled',
      design.timeSlotDisabled,
    );
    root.style.setProperty(
      '--brand-time-slot-text-disabled',
      design.timeSlotTextDisabled,
    );
    root.style.setProperty(
      '--brand-time-slot-hover-text',
      design.timeSlotHoverText || design.textSecondary,
    );
    root.style.setProperty(
      '--brand-time-slot-selected-text',
      design.timeSlotSelectedText || design.textSecondary,
    );

    // Police
    root.style.setProperty(
      '--brand-font-family',
      `"${design.fontFamily}", sans-serif`,
    );

    // Poids de la police par type
    root.style.setProperty(
      '--brand-font-weight-title',
      design.fontWeight.title,
    );
    root.style.setProperty(
      '--brand-font-weight-subtitle',
      design.fontWeight.subtitle,
    );
    root.style.setProperty('--brand-font-weight-body', design.fontWeight.body);
    root.style.setProperty(
      '--brand-font-weight-button',
      design.fontWeight.button,
    );
    root.style.setProperty(
      '--brand-font-weight-label',
      design.fontWeight.label,
    );
    root.style.setProperty(
      '--brand-font-weight-caption',
      design.fontWeight.caption,
    );
    root.style.setProperty(
      '--brand-font-weight-calendar-day',
      design.fontWeight.calendarDay,
    );
    root.style.setProperty(
      '--brand-font-weight-time-slot',
      design.fontWeight.timeSlot,
    );
    root.style.setProperty(
      '--brand-font-weight-time-slot-selected',
      design.fontWeight.timeSlotSelected || design.fontWeight.timeSlot,
    );
    root.style.setProperty(
      '--brand-font-weight-calendar-date',
      design.fontWeight.calendarDate || design.fontWeight.title,
    );

    // Taille de la police par type
    root.style.setProperty('--brand-font-size-title', design.fontSize.title);
    root.style.setProperty(
      '--brand-font-size-subtitle',
      design.fontSize.subtitle,
    );
    root.style.setProperty('--brand-font-size-body', design.fontSize.body);
    root.style.setProperty('--brand-font-size-button', design.fontSize.button);
    root.style.setProperty('--brand-font-size-label', design.fontSize.label);
    root.style.setProperty(
      '--brand-font-size-caption',
      design.fontSize.caption,
    );
    root.style.setProperty(
      '--brand-font-size-calendar-day',
      design.fontSize.calendarDay,
    );
    root.style.setProperty(
      '--brand-font-size-time-slot',
      design.fontSize.timeSlot,
    );

    // Boutons
    root.style.setProperty(
      '--brand-button-border-radius',
      design.buttonBorderRadius,
    );
    root.style.setProperty(
      '--brand-calendar-day-border-radius',
      design.calendarDayBorderRadius,
    );
    root.style.setProperty(
      '--brand-time-slot-border-radius',
      design.timeSlotBorderRadius,
    );

    // Ajouter une classe au body pour identifier la marque
    document.body.className = `brand-${brandConfig.design.name.toLowerCase()}`;
  }, [brandConfig]);

  return <>{children}</>;
};

BrandStyleProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default BrandStyleProvider;
